// src/components/InvestmentDetailPage.js - DESIGN HARMONISÉ AVEC REACT BOOTSTRAP (CORRIGÉ ET STABILISÉ)

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Container, Row, Col, Card, Button, ProgressBar, Spinner, Alert } from 'react-bootstrap';
import { FaCheckCircle, FaSpinner, FaDollarSign, FaUsers, FaLink, FaClipboard } from 'react-icons/fa'; 

// --- (COMPOSANTS PHANTOM NODE ET CHAIN STRUCTURE EN BOOTSTRAP) ---

/**
 * Composant pour afficher un placeholder de slot vide (équivalent PhantomNode)
 */
const PhantomNode = ({ level }) => (
    <div 
        className={`text-center border border-dashed border-secondary bg-light text-muted p-2 rounded-lg`}
        style={{ 
            minHeight: level === 3 ? '55px' : '70px',
            minWidth: level === 3 ? '100px' : '150px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '5px' 
        }}
    >
        <span className="small fst-italic">Slot Disponible</span>
    </div>
);

/**
 * Composant pour afficher un noeud utilisateur dans la chaîne
 */
const NodeComponent = ({ user, level, color }) => {
    // CORRECTION: Isoler les classes dynamiques dans des variables pour la stabilité
    const bgColorClass = `bg-${color}`;
    const textColorClass = `text-${color}`;
    
    return (
        <div 
            className={`p-3 ${bgColorClass} text-white rounded shadow-sm text-center`}
            style={{ 
                minWidth: level === 3 ? '100px' : '150px',
                minHeight: level === 3 ? '55px' : '70px',
                margin: '5px' 
            }}
        >
            <span className={`fw-bold ${level === 3 ? 'small' : 'fs-6'}`}>{user.username}</span><br />
            <span className={`fw-light ${level === 3 ? 'smaller' : 'small'}`}>
                {level === 1 ? 'Votre Position (L1)' : `Niveau ${level}`}
            </span>
            {level === 2 && (
                <span className={`badge bg-white ${textColorClass} fw-bold mt-1 d-block mx-auto w-fit`}>
                    {user.sponsoredCount || 0}/2
                </span>
            )}
        </div>
    );
};

/**
 * Composant pour afficher la structure de la chaîne
 */
const ChainStructure = ({ chainDetails }) => {
    if (!chainDetails || !chainDetails.level2) return null;

    // Définitions des couleurs Bootstrap (DOIVENT ÊTRE DES NOMS DE VARIANTS VALIDES)
    const colorPrimary = 'primary'; 
    const colorLevel2 = 'info';     
    const colorLevel3 = 'success';  

    return (
        <Card className="shadow-lg p-4 mt-5 border-0">
            <Card.Header className="bg-white border-0">
                 <h5 className="mb-0 fw-bolder text-dark border-bottom pb-2">Arbre de Parrainage (Chaîne 1-2-4)</h5>
            </Card.Header>
            <Card.Body className="overflow-auto pt-4">

                {/* Niveau 1 : Investissement de l'utilisateur actuel */}
                <Row className="justify-content-center mb-4">
                    <Col xs="auto">
                        <NodeComponent user={chainDetails} level={1} color={colorPrimary} />
                    </Col>
                </Row>

                {/* Niveau 2 & 3 */}
                <Row className="justify-content-center flex-nowrap" style={{ minWidth: '700px' }}>
                    {/* CORRECTION: Stabiliser la clé pour le niveau 2 */}
                    {chainDetails.level2.map((l2, index) => (
                        <Col 
                            key={l2.id || `l2-${index}`} 
                            xs={6} 
                            className="text-center d-flex flex-column align-items-center p-0 mx-2"
                        >
                            
                            {/* Niveau 2 */}
                            <div className="mb-3">
                                {l2.isPhantom ? (
                                    <PhantomNode level={2} />
                                ) : (
                                    <NodeComponent user={l2} level={2} color={colorLevel2} />
                                )}
                            </div>
                            
                            {/* Niveau 3 (sous le Niveau 2) */}
                            <div className="d-flex justify-content-center w-100 gap-1"> 
                                {l2.level3.map((l3, subIndex) => (
                                    <div 
                                        // CORRECTION: Stabiliser la clé pour le niveau 3
                                        key={l3.id || `l3-${index}-${subIndex}`}
                                    >
                                        {l3.isPhantom ? (
                                            <PhantomNode level={3} />
                                        ) : (
                                            <NodeComponent user={l3} level={3} color={colorLevel3} />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </Col>
                    ))}
                </Row>

                <p className="text-center text-muted small mt-5 pt-3 border-top">
                    Remplissez **6 slots** aux Niveaux 2 et 3 avec le même plan pour boucler la chaîne.
                </p>
            </Card.Body>
        </Card>
    );
};

// --- (FIN DES COMPOSANTS DE LOGIQUE) ---


const InvestmentDetailPage = () => {
    const { id } = useParams(); 
    const [investment, setInvestment] = useState(null);
    const [loading, setLoading] = useState(true);
    const [copySuccess, setCopySuccess] = useState(''); // Maintenu pour la fonction, mais l'UI est supprimée.
    
    
    useEffect(() => {
        const fetchDetails = async () => {
            try {
                const token = localStorage.getItem('token');
                const config = { headers: { 'Authorization': `Bearer ${token}` } };
                // Utilisation de l'API chain-details pour récupérer toutes les données nécessaires
                const res = await axios.get(`/api/investment/chain-details/${id}`, config); 
                setInvestment(res.data);
            } catch (error) {
                const status = error.response ? error.response.status : 'N/A';
                let message = "Impossible de charger les détails de l'investissement.";
                if (status === 404) message = "Investissement introuvable ou route erronée.";
                else if (status === 401 || status === 403) message = "Accès refusé. Votre session a peut-être expiré.";
                setInvestment({ error: true, message });
            } finally {
                setLoading(false);
            }
        };

        fetchDetails();
    }, [id]);

    // On utilise le lien actuel de la page de suivi comme lien de partage
    const currentURL = window.location.href; 
    
    // Maintenu même si le bouton est supprimé, au cas où vous souhaiteriez le réintégrer.
    const handleCopy = () => {
        navigator.clipboard.writeText(currentURL);
        setCopySuccess('Lien copié !');
        setTimeout(() => setCopySuccess(''), 2000);
    };

    const formatCurrency = (amount) => {
        return amount ? parseFloat(amount).toLocaleString('fr-FR', { style: 'currency', currency: 'XOF' }) : '0 FCFA';
    };

    if (loading) return (
        <Container className="text-center mt-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2">Chargement des détails...</p>
        </Container>
    );
    
    if (investment && investment.error) {
        return (
            <Container className="mt-5">
                <Alert variant="danger" className="text-center">
                    <h3 className="mb-2">Erreur de Chargement 🛑</h3>
                    <p>{investment.message}</p>
                </Alert>
            </Container>
        );
    }

    if (!investment || !investment.level1) return <Container className="text-center mt-5"><p className="text-muted">Détails de l'investissement introuvables.</p></Container>;

    const chainDetails = investment.level1;
    const progress = chainDetails.progress || 0; 
    const totalRequired = chainDetails.totalRequired || 6;
    const completionPercentage = Math.round((progress / totalRequired) * 100);
    const isCompleted = chainDetails.isCompleted || false;

    return (
        <Container className="py-4">
            
            {/* EN-TÊTE ET TITRE */}
            <Row className="mb-4">
                <Col>
                    <h1 className="fw-bolder text-primary border-bottom border-primary pb-2">
                        Suivi de votre Chaîne d'Investissement
                    </h1>
                    <p className="text-muted small">ID d'Investissement: {id}</p>
                </Col>
            </Row>
            
            {/* CARTES STATISTIQUES (3 colonnes) */}
            <Row className="g-3 mb-4">
                
                {/* CARD 1: Statut Général */}
                <Col md={4}>
                    <Card className={`p-3 text-white shadow-sm border-0 ${isCompleted ? 'bg-success' : 'bg-primary'}`}>
                        <Card.Body>
                            <p className="small mb-1 opacity-75 fw-light">Statut de la Chaîne</p>
                            <h4 className="fw-bolder mb-0">
                                {isCompleted ? 'COMPLÉTÉE 🎉' : 'EN ATTENTE...'}
                            </h4>
                            {isCompleted && <p className="mt-2 small fw-semibold">Gain Double Débloqué !</p>}
                        </Card.Body>
                    </Card>
                </Col>

                {/* CARD 2: Montant et Plan */}
                <Col md={4}>
                    <Card className="p-3 shadow-sm border-start border-3 border-info">
                        <Card.Body>
                            <p className="small text-muted mb-1">Montant Investi</p>
                            <h4 className="fw-bolder text-dark mb-0">
                                <FaDollarSign className="me-2 text-info" />
                                {formatCurrency(chainDetails.amount)}
                            </h4>
                            <p className="text-info fw-semibold mt-2 mb-0 small">Plan: {chainDetails.planName || 'N/A'}</p>
                        </Card.Body>
                    </Card>
                </Col>

                {/* CARD 3: Progression */}
                <Col md={4}>
                    <Card className="p-3 shadow-sm border-start border-3 border-warning">
                        <Card.Body>
                            <p className="small text-muted mb-3">Progression du Parrainage</p>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <span className="fs-5 fw-bold text-dark">
                                    <FaUsers className="me-2 text-warning" />
                                    {progress}/{totalRequired}
                                </span>
                                <span className="fs-5 fw-bolder text-warning">
                                    {completionPercentage}%
                                </span>
                            </div>
                            <ProgressBar 
                                now={completionPercentage} 
                                variant={isCompleted ? 'success' : 'warning'} 
                                style={{ height: '8px' }}
                            />
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
            
            {/* 🛑 SECTION LIEN DE LA PAGE D'INVESTISSEMENT RETIRÉE 🛑 */}
            {/* Si vous souhaitez ajouter un autre élément ici, insérez-le à cet endroit. */}

            {/* Diagramme de la chaîne (structure complexe maintenue) */}
            <ChainStructure chainDetails={chainDetails} />

        </Container>
    );
};

export default InvestmentDetailPage;